import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Container,
  Box,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Grid,
  Pagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import SurgeryDetailsDialog from "../TestsTobeDone/SurgeryDetailsDialog";
import { useTranslation } from "react-i18next";

const SurgeryList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [selectedCoDoctor, setSelectedCoDoctor] = useState("");
  const [selectedAnesthetist, setSelectedAnesthetist] = useState("");
  const [selectedSurgeryType, setSelectedSurgeryType] = useState("");
  const [selectedSurgery, setSelectedSurgery] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Pagination States
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalSurgeries, setTotalSurgeries] = useState(0);

  // Debounced Search Term to prevent excessive re-renders
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);

  // Fetch surgeries based on filters
  const filters = useMemo(() => {
    return [
      debouncedSearch ? `search=${encodeURIComponent(debouncedSearch)}` : "",
      selectedDoctor ? `doctor=${selectedDoctor}` : "",
      selectedCoDoctor ? `co_doctor=${selectedCoDoctor}` : "",
      selectedAnesthetist ? `anesthetist=${selectedAnesthetist}` : "",
      selectedSurgeryType ? `surgery_type=${selectedSurgeryType}` : "",
      `page=${page}`,
      `page_size=${rowsPerPage}`,
    ]
      .filter(Boolean)
      .join("&");
  }, [
    debouncedSearch,
    selectedDoctor,
    selectedCoDoctor,
    selectedAnesthetist,
    selectedSurgeryType,
    page,
    rowsPerPage,
  ]);

  const { data: surgeries, refetch } = useFetchObjects(
    ["surgeries", filters],
    `surgeries/?${filters}`,
    token
  );

  // Fetch doctors list for filters
  const { data: doctors = [] } = useFetchObjects(
    ["doctors"],
    "users/?role=doctor",
    token
  );

  // Debounce the search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500); // 500ms debounce delay

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Refetch surgeries when filters change
  useEffect(() => {
    refetch();
  }, [refetch, debouncedSearch, page, rowsPerPage]);

  // Update total surgeries count
  useEffect(() => {
    if (surgeries && surgeries.count !== undefined) {
      setTotalSurgeries(surgeries.count);
    }
  }, [surgeries]);

  // Handle page change
  const handlePageChange = useCallback((event, value) => {
    setPage(value);
  }, []);

  // Handle rows per page change
  const handleRowsPerPageChange = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  }, []);

  // Open Surgery Details Dialog
  const handleOpenDialog = useCallback((surgery) => {
    setSelectedSurgery(surgery);
    setDialogOpen(true);
  }, []);

  // Close Surgery Details Dialog
  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setSelectedSurgery(null);
  }, []);

  // Reset all filters
  const handleResetFilters = useCallback(() => {
    setSearchTerm("");
    setSelectedDoctor("");
    setSelectedCoDoctor("");
    setSelectedAnesthetist("");
    setSelectedSurgeryType("");
  }, []);

  return (
    <Container maxWidth="xl" sx={{ mt: 5 }}>
      <Paper elevation={4} sx={{ p: 4, borderRadius: 2, width: "100%" }}>
        <Typography variant="h4" align="center" fontWeight="bold" gutterBottom>
          {t("operations.surgeryList")}
        </Typography>

        {/* Search and Filter Section */}
        <Box sx={{ mb: 4 }}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: "#f7f7f7",
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <Grid container spacing={2} alignItems="center">
              {/* Search by Patient Name */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label={t("operations.searchByPatientName")}
                  variant="outlined"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <IconButton>
                        <SearchIcon />
                      </IconButton>
                    ),
                  }}
                  fullWidth
                />
              </Grid>

              {/* Filter by Doctor */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel>{t("operations.filterByDoctor")}</InputLabel>
                  <Select
                    value={selectedDoctor}
                    onChange={(e) => setSelectedDoctor(e.target.value)}
                    label={t("operations.filterByDoctor")}
                  >
                    <MenuItem value="">
                      <em>{t("operations.allDoctors")}</em>
                    </MenuItem>
                    {doctors.map((doc) => (
                      <MenuItem key={doc.id} value={doc.id}>
                        {doc.first_name} {doc.last_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Filter by Co-Doctor */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel>{t("operations.filterByCoDoctor")}</InputLabel>
                  <Select
                    value={selectedCoDoctor}
                    onChange={(e) => setSelectedCoDoctor(e.target.value)}
                    label={t("operations.filterByCoDoctor")}
                  >
                    <MenuItem value="">
                      <em>{t("operations.allCoDoctors")}</em>
                    </MenuItem>
                    {doctors.map((doc) => (
                      <MenuItem key={doc.id} value={doc.id}>
                        {doc.first_name} {doc.last_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Filter by Anesthetist */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel>
                    {t("operations.filterByCoAnesthetist")}
                  </InputLabel>
                  <Select
                    value={selectedAnesthetist}
                    onChange={(e) => setSelectedAnesthetist(e.target.value)}
                    label={t("operations.filterByCoAnesthetist")}
                  >
                    <MenuItem value="">
                      <em>{t("operations.allAnesthetist")}</em>
                    </MenuItem>
                    {doctors.map((doc) => (
                      <MenuItem key={doc.id} value={doc.id}>
                        {doc.first_name} {doc.last_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Filter by Surgery Type */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel>{t("operations.filterBySurgeryType")}</InputLabel>
                  <Select
                    value={selectedSurgeryType}
                    onChange={(e) => setSelectedSurgeryType(e.target.value)}
                    label={t("operations.filterBySurgeryType")}
                  >
                    <MenuItem value="">
                      <em>{t("operations.allType")}</em>
                    </MenuItem>
                    <MenuItem value={1}>{t("operations.internal")}</MenuItem>
                    <MenuItem value={2}>{t("operations.referred")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Reset Filters Button */}
              <Grid item xs={12} display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleResetFilters}
                  sx={{ ml: 2 }}
                >
                  {t("operations.resetFilters")}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* Surgery Table */}
        <TableContainer component={Paper} elevation={3} sx={{ width: "100%" }}>
          <Table sx={{ tableLayout: "fixed", minWidth: 650 }}>
            <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
              <TableRow>
                {/* ID Column */}
                <TableCell
                  align="center"
                  sx={{
                    width: "60px",
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("operations.id", "ID")}
                </TableCell>

                {/* Patient Name */}
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    width: "150px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("operations.patientName")}
                </TableCell>

                {/* Ticket */}
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    width: "120px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("operations.ticket", { defaultValue: "Ticket" })}
                </TableCell>

                {/* Surgery Type */}
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    width: "120px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("operations.surgeryType")}
                </TableCell>

                {/* Surgery Date */}
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    width: "120px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("operations.surgeryDate")}
                </TableCell>

                {/* Doctor */}
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    width: "120px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("operations.doctor")}
                </TableCell>

                {/* Co-Doctor */}
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    width: "120px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("operations.coDoctor")}
                </TableCell>

                {/* Anesthetist */}
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    width: "120px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("operations.anesthetist")}
                </TableCell>

                {/* Status */}
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    width: "100px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("operations.status")}
                </TableCell>

                {/* Total Amount */}
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    width: "100px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("operations.totalAmount")}
                </TableCell>

                {/* Actions */}
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    width: "100px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("operations.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ bgcolor: "#ccfcfc" }}>
              {surgeries?.results.map((surgery) => (
                <TableRow key={surgery.id} hover>
                  {/* ID Column */}
                  <TableCell
                    align="center"
                    sx={{ width: "60px", whiteSpace: "nowrap" }}
                  >
                    {surgery.patient}
                  </TableCell>

                  {/* Patient Name */}
                  <TableCell
                    align="center"
                    sx={{ width: "150px", whiteSpace: "nowrap" }}
                  >
                    {surgery.patient_display || "N/A"}
                  </TableCell>

                  {/* Ticket */}
                  <TableCell
                    align="center"
                    sx={{ width: "120px", whiteSpace: "nowrap" }}
                  >
                    {surgery.ticket_number_display || "—"}
                  </TableCell>

                  {/* Surgery Type */}
                  <TableCell
                    align="center"
                    sx={{ width: "120px", whiteSpace: "nowrap" }}
                  >
                    {surgery.surgery_type_display || "N/A"}
                  </TableCell>

                  {/* Surgery Date */}
                  <TableCell
                    align="center"
                    sx={{ width: "120px", whiteSpace: "nowrap" }}
                  >
                    {surgery.surgery_date
                      ? new Date(surgery.surgery_date).toLocaleDateString()
                      : "N/A"}
                  </TableCell>

                  {/* Doctor */}
                  <TableCell
                    align="center"
                    sx={{ width: "120px", whiteSpace: "nowrap" }}
                  >
                    {surgery.doctor_display || "N/A"}
                  </TableCell>

                  {/* Co-Doctor */}
                  <TableCell
                    align="center"
                    sx={{ width: "120px", whiteSpace: "nowrap" }}
                  >
                    {surgery.co_doctor_display || "N/A"}
                  </TableCell>

                  {/* Anesthetist */}
                  <TableCell
                    align="center"
                    sx={{ width: "120px", whiteSpace: "nowrap" }}
                  >
                    {surgery.anesthetist_display || "N/A"}
                  </TableCell>

                  {/* Status */}
                  <TableCell
                    align="center"
                    sx={{ width: "100px", whiteSpace: "nowrap" }}
                  >
                    {surgery.surgery_state_display || "N/A"}
                  </TableCell>

                  {/* Total Amount */}
                  <TableCell
                    align="center"
                    sx={{ width: "100px", whiteSpace: "nowrap" }}
                  >
                    {surgery.total_surgery_amount
                      ? `؋${surgery.total_surgery_amount}`
                      : "N/A"}
                  </TableCell>

                  {/* Actions */}
                  <TableCell
                    align="center"
                    sx={{ width: "100px", whiteSpace: "nowrap" }}
                  >
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleOpenDialog(surgery)}
                      size="small"
                    >
                      {t("operations.details")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}

              {/* Display message when no surgeries are found */}
              {surgeries?.results.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    {t("operations.noSurgeryFound")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {/* Pagination Controls */}
          <Box
            display="flex"
            justifyContent="space-between"
            mt={4}
            mb={2}
            ml={2}
          >
            <Box display="flex" alignItems="space-between">
              <Typography variant="body2" mr={2}>
                {t("reception.rowsPerPage")}:
              </Typography>
              <FormControl variant="standard" sx={{ minWidth: 120 }}>
                <Select
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  label={t("reception.rowsPerPage")}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={500}>500</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box>
              <Pagination
                count={Math.ceil(totalSurgeries / rowsPerPage)}
                page={page}
                onChange={handlePageChange}
                color="primary"
                variant="outlined"
                shape="rounded"
              />
            </Box>
          </Box>
        </TableContainer>
      </Paper>

      {/* Surgery Details Dialog */}
      <SurgeryDetailsDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        surgery={selectedSurgery}
      />
    </Container>
  );
};

export default SurgeryList;
